import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

interface PageAuthorizationResponse {
  accessLevel: 'view' | 'edit' | 'none';
  canEdit: boolean;
  canView: boolean;
}

/**
 * Hook that checks the current user's access level for a specific page.
 *
 * Calls GET /page-access/:pageSlug/authorization on mount and returns the result.
 * Defaults to { accessLevel: 'view', canEdit: false, canView: true, isLoading: false } on any
 * network or server error (fail-closed).
 */
export function usePageAuthorization(pageSlug: string): {
  accessLevel: 'view' | 'edit' | 'none';
  canEdit: boolean;
  canView: boolean;
  isLoading: boolean;
} {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [accessLevel, setAccessLevel] = useState<'view' | 'edit' | 'none'>('view');
  const [canEdit, setCanEdit] = useState(false);
  const [canView, setCanView] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function checkAuthorization() {
      try {
        const data = await apiClient<PageAuthorizationResponse>(
          `/page-access/${pageSlug}/authorization`,
        );
        if (!cancelled) {
          setAccessLevel(data.accessLevel);
          setCanEdit(data.canEdit);
          setCanView(data.canView);
        }
      } catch {
        // Fail-closed: default to view-only on any error
        if (!cancelled) {
          setAccessLevel('view');
          setCanEdit(false);
          setCanView(true);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    checkAuthorization();

    return () => {
      cancelled = true;
    };
  }, [pageSlug]);

  // Redirect when access is denied
  useEffect(() => {
    if (isLoading) return;
    if (canView) return;

    const target = user?.role === 'Attendee' ? '/my-weekend' : '/dashboard';
    navigate(target, { replace: true });
  }, [isLoading, canView, user?.role, navigate]);

  return { accessLevel, canEdit, canView, isLoading };
}
